/* Section « Plateforme » d'un membre actif (individuel ou groupe) : voyants Compte et Onboarding, envois. */
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, Mail, Send } from 'lucide-react';
import type { AccountState, CrmContact, CrmContactDraft, SharingOperation } from './types';
import { sendInvitation, sendWelcome } from './api';
import { StatusDot, formatDateFr, errorMessage } from './ui';

interface Props {
  /** Valeurs affichées (fiche en cours d'édition). */
  contact: CrmContact;
  account?: AccountState;
  operations: SharingOperation[];
  /** La fiche a des modifications non enregistrées : les envois sont refusés. */
  dirty: boolean;
  /** Enregistre la date d'envoi dans la fiche. */
  onPatched: (patch: CrmContactDraft, message: string) => Promise<void>;
  /** Boutons supplémentaires affichés avant les envois. */
  extraActions?: React.ReactNode;
}

export function PlatformSection({ contact: c, account, operations, dirty, onPatched, extraActions }: Props) {
  const [busy, setBusy] = useState<'welcome' | 'invite' | null>(null);

  const run = async (key: 'welcome' | 'invite', fn: () => Promise<void>) => {
    if (dirty) { toast.error('Enregistrez d’abord la fiche'); return; }
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const handleWelcome = () => run('welcome', async () => {
    if (c.onboarding_sent_at && !window.confirm('Le message de bienvenue a déjà été envoyé. Le renvoyer ?')) return;
    await sendWelcome(c, operations);
    await onPatched({ onboarding_sent_at: new Date().toISOString() }, 'Message de bienvenue envoyé');
  });

  const handleInvite = () => run('invite', async () => {
    if (c.invitation_sent_at && !window.confirm('Une invitation a déjà été envoyée. La renvoyer ?')) return;
    await sendInvitation(c, operations);
    await onPatched({ invitation_sent_at: new Date().toISOString() }, 'Invitation envoyée');
  });

  const icon = (key: 'welcome' | 'invite', Icon: React.ElementType) =>
    busy === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />;

  const noEmail = !c.email?.trim();
  const primaryBtn = 'mt-2.5 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-[11px] font-semibold text-neutral-900 disabled:opacity-50';
  const linkBtn = 'mt-2 inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-800 disabled:opacity-50';

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {/* Compte : même règle que le tableau de bord admin (accès plateforme créé ou non) */}
        <div className={`p-3 rounded-xl border flex flex-col ${account === 'approved' ? 'border-emerald-200 bg-emerald-50' : account === 'pending' ? 'border-amber-200 bg-amber-50' : 'border-neutral-200 bg-neutral-50'}`}>
          <StatusDot on={account === 'approved'} pending={account === 'pending'} label={account === 'approved' ? 'Compte activé' : account === 'pending' ? 'Compte en attente' : 'Compte non activé'} />
          <p className={`text-[11px] mt-1 ${account === 'approved' ? 'text-emerald-700' : account === 'pending' ? 'text-amber-800' : 'text-neutral-500'}`}>
            {account === 'approved' ? 'Le membre se connecte à la plateforme'
              : account === 'pending' ? 'Compte créé, à valider dans le tableau de bord'
                : c.invitation_sent_at ? `Invitation envoyée le ${formatDateFr(c.invitation_sent_at)}, pas encore de compte` : 'Aucun accès plateforme créé'}
          </p>
          {account !== 'approved' && (
            noEmail
              ? <p className="mt-2 text-[11px] text-amber-700">Ajoutez un e-mail pour envoyer l’invitation.</p>
              : <button onClick={handleInvite} disabled={!!busy} className={c.invitation_sent_at ? linkBtn : primaryBtn}>
                  {icon('invite', Mail)}{c.invitation_sent_at ? 'Renvoyer l’invitation' : 'Envoyer l’invitation'}
                </button>
          )}
        </div>
        <div className={`p-3 rounded-xl border flex flex-col ${c.onboarding_sent_at ? 'border-emerald-200 bg-emerald-50' : 'border-neutral-200 bg-neutral-50'}`}>
          <StatusDot on={!!c.onboarding_sent_at} label={c.onboarding_sent_at ? 'Onboarding fait' : 'Onboarding à faire'} />
          <p className={`text-[11px] mt-1 ${c.onboarding_sent_at ? 'text-emerald-700' : 'text-neutral-500'}`}>
            {c.onboarding_sent_at ? `le ${formatDateFr(c.onboarding_sent_at)}` : 'Le message de bienvenue n’est pas encore parti'}
          </p>
          {noEmail && !c.onboarding_sent_at
            ? <p className="mt-2 text-[11px] text-amber-700">Ajoutez un e-mail pour lancer l’onboarding.</p>
            : !noEmail && (
              <button onClick={handleWelcome} disabled={!!busy} className={c.onboarding_sent_at ? linkBtn : primaryBtn}>
                {icon('welcome', Send)}{c.onboarding_sent_at ? 'Renvoyer le message de bienvenue' : 'Lancer l’onboarding'}
              </button>
            )}
        </div>
      </div>
      {extraActions && <div className="flex flex-wrap gap-2">{extraActions}</div>}
      <p className="text-[11px] text-neutral-400">Les textes se modifient dans CRM → Paramètres.</p>
    </div>
  );
}
